// Única fuente del copy del héroe B2 (D-22): todo sale palabra por palabra de Heroe_B2.dc.html y
// Heroe_B2_Movil.dc.html, para que el texto del servidor, la animación y los tests lean lo mismo.
// Dos frases marcadas por el dueño se quedan tal cual (D-29): «Interpreta el BDI-II que puntué ayer»
// y «BDI-II · 16 puntos · puntuado por ti».
//
// H-1 (corrección de contenido, D-22 gana al lienzo): la pregunta 2 del tablero pedía un «Informe de
// evolución» hecho con una plantilla de ese nombre, que no existe. Ahora pide el informe de
// seguimiento, que es uno de los cuatro documentos por secciones del catálogo
// (catalogoFuncionesE1.ts, Poderes_Demo), y lo describe como se redacta: sección a sección.
//
// El marco del asistente (cabecera, compositor, caja de propuesta) es el mismo que el de
// Poderes_Demo y sale de textosAsistenteEjemplo.ts; el contador de la biblioteca completa dice lo
// mismo que el de la sección Biblioteca, porque es la misma biblioteca.

import { contadorRegistros, TEXTOS_SECCION } from '@/components/marketing/biblioteca/textosBiblioteca';
import { MARCO_ASISTENTE } from '@/components/marketing/comun/textosAsistenteEjemplo';

export const TITULAR_HEROE = { antes: 'Todo lo que sabes de tu paciente, ', resaltado: 'conectado.' } as const;

export const ENTRADILLA_HEROE =
    'Sesiones, notas, tests y documentos se enlazan en un solo tablero. Pregunta al asistente y te responde citando la sesión de la que sale cada dato. La IA describe y tú decides.';

export const CTA_HEROE = {
    primario: { texto: 'Empieza tu prueba gratuita', href: '/app/register' },
    secundario: { texto: 'Ver cómo funciona', href: '#app-por-dentro' },
} as const;

export const SOCIAL_HEROE = {
    destacado: 'Únete a los más de 100 psicólogos',
    restoEscritorio: ' que ya han probado TherapyRecap · 7 días gratis, sin tarjeta',
    restoMovil: ' que ya han probado TherapyRecap',
    segundaLineaMovil: '7 días gratis · sin tarjeta',
    iniciales: ['LM', 'JS', 'CA', 'EC'],
} as const;

export const ARIA_ESCENARIO =
    'Animación de ejemplo con la paciente Marta Ruiz: sesión a sesión, todo lo que el terapeuta registra se va colocando en una biblioteca circular alrededor de la paciente, ordenada por funciones. Después el asistente responde a cinco peticiones seguidas y en cada una localiza el registro exacto que usa, o guarda uno nuevo.';

export const ESCENARIO_HEROE = {
    titulo: 'La memoria de Marta Ruiz',
    iniciales: 'MR',
    asistente: MARCO_ASISTENTE.nombre,
    alcance: MARCO_ASISTENTE.alcance,
    vacio: 'Cada sesión, nota y test que registras se suma a la memoria de Marta. Pregunta lo que quieras: el asistente busca en todo su historial.',
    funcionesBoton: MARCO_ASISTENTE.funciones,
    marcadorEscritorio: MARCO_ASISTENTE.marcador,
    marcadorMovil: 'Pregunta o pide algo',
    primeraSesion: 'Primera sesión…',
    incibe: 'Finalista de INCIBE Emprende Ventures',
    financiacion: 'Financiado por la Unión Europea · NextGenerationEU',
    pausar: 'Pausar animación',
} as const;

// El contador del escenario: la biblioteca completa y, durante el acto 1, la sesión que se registra.
export const CONTADOR_HEROE = {
    completo: contadorRegistros,
    enObra: (sesion: number, registros: number) => `Sesión ${sesion} de ${TEXTOS_SECCION.sesiones} · ${registros} registros`,
} as const;

// Controles del vídeo del héroe de escritorio (18-09, UI-SPEC §Copywriting). Los de sonido vienen
// del antiguo HeroVideo. La pausa tiene nombre fijo (aria-pressed lleva el estado): `pausarVideo` con
// el vídeo y `pausar` de ESCENARIO_HEROE en el respaldo.
export const CONTROLES_VIDEO_HEROE = {
    activarSonido: 'Activar sonido',
    silenciar: 'Silenciar',
    ariaActivarSonido: 'Activar el sonido del vídeo',
    ariaSilenciar: 'Silenciar el vídeo',
    pausarVideo: 'Pausar vídeo',
} as const;

// Orden de funciones() del tablero: el índice es el sector de la biblioteca.
export const FUNCIONES_HEROE = [
    'Sesiones',
    'Plantillas',
    'Informes',
    'Psicometría',
    'Manuscritos',
    'Plan de tratamiento',
    'Notas fijas',
    'Carpetas',
] as const;

export interface PreguntaHeroe {
    readonly pregunta: string;
    readonly pensando: string;
    readonly etiquetaLee: string;
    readonly etiquetaEscribe: string;
}

export const PREGUNTAS_HEROE: readonly PreguntaHeroe[] = [
    { pregunta: '¿Cómo ha dormido Marta estas semanas?', pensando: 'Buscando en las 12 sesiones…', etiquetaLee: 'Sesión 2', etiquetaEscribe: '' },
    {
        pregunta: 'Prepárame un informe de seguimiento para su médica de cabecera',
        pensando: 'Redactando el informe sección a sección…',
        etiquetaLee: 'Sesiones',
        etiquetaEscribe: 'Informe nuevo',
    },
    { pregunta: 'Interpreta el BDI-II que puntué ayer', pensando: 'Leyendo la escala puntuada…', etiquetaLee: 'BDI-II', etiquetaEscribe: '' },
    { pregunta: '¿Qué objetivo añadirías al plan de tratamiento?', pensando: 'Revisando el plan de tratamiento…', etiquetaLee: 'Plan actual', etiquetaEscribe: '' },
    { pregunta: 'Recuerda que Marcos es su hermano, no su pareja', pensando: 'Guardando la nota fija…', etiquetaLee: '', etiquetaEscribe: 'Nota fija' },
];

export const RESPUESTAS_HEROE = {
    sueno: 'Sigue tardando en dormirse, aunque ya no se despierta de madrugada',
    cita: '[S2]',
    explicacionCitaEscritorio: '[S2] es la sesión 2: la cita lleva a ella',
    explicacionCitaMovil: '[S2] lleva a la sesión 2',
    informeTituloEscritorio: 'Informe de seguimiento · borrador',
    informeSubtituloEscritorio: 'Se redacta sección a sección',
    informeTituloMovil: 'Informe de seguimiento',
    informeSubtituloMovil: 'Borrador · por secciones',
    secciones: ['1. Motivo de consulta', '2. Evolución', '3. Evaluación', '4. Recomendaciones'],
    seccionesRestantesMovil: ' · y 2 secciones más',
    lecturaEtiqueta: 'Lectura descriptiva',
    lecturaTitulo: 'BDI-II · 16 puntos · puntuado por ti',
    // 17-REVIEW-C CR-02: la app no aplica puntos de corte; describe cómo cambia la puntuación y la lectura es tuya.
    lecturaTexto: 'Baja respecto a la aplicación anterior; tú la interpretas.',
    propuestaEscritorio: MARCO_ASISTENTE.propuestaPendiente,
    propuestaMovil: 'Propuesta · pendiente',
    propuestaTitulo: 'Añadir al plan el objetivo «Dormirse con menos dificultad»',
    propuestaBotones: MARCO_ASISTENTE.botonesPropuesta,
    nadaSeAplicaSolo: 'Nada se aplica solo.',
    notaFija: 'Anotado. Lo tendré siempre en cuenta.',
    notasFijasChip: 'Notas fijas (1)',
} as const;
